import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UploadCloud, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useUiStore } from '@/stores/uiStore';
import { apiError } from '@/lib/api-client';
import { useWorkImportParse, useImportCommit, type WorkParseResponse } from './useImport';

type Step = 'upload' | 'preview' | 'done';

/**
 * Works & expenses import (Pnerp_*_3.xlsx: `baza` + `arhiv` sheets). Kept as a
 * self-contained panel — like the shapefile panel — because its preview shape
 * differs from the parcel/photo xlsx flow.
 */
export function WorkImportPanel() {
  const { t } = useTranslation(['works', 'import', 'common']);
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [entity, setEntity] = useState('');
  const [parsed, setParsed] = useState<WorkParseResponse | null>(null);
  const [committed, setCommitted] = useState<{ imported: number; skipped: number } | null>(null);

  const parseMut = useWorkImportParse();
  const commitMut = useImportCommit();

  const onParse = async () => {
    if (!file) return;
    try {
      const res = await parseMut.mutateAsync({ file, entitateSilvica: entity.trim() || undefined });
      setParsed(res);
      setStep('preview');
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  const onCommit = async () => {
    if (!parsed) return;
    try {
      const res = await commitMut.mutateAsync({ batchId: parsed.batchId, mode: 'skip_invalid' });
      setCommitted({ imported: res.imported, skipped: res.skipped });
      setStep('done');
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  if (step === 'done' && committed) {
    return (
      <Card>
        <CardBody className="space-y-4 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
          <div className="text-lg font-semibold">{committed.imported} + / {committed.skipped} −</div>
          <Button onClick={() => navigate('/app/works')}>{t('works:actions.back')}</Button>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="text-sm font-semibold">{t('works:actions.import')} · Pnerp_*_3.xlsx</CardHeader>
        <CardBody className="space-y-3">
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 p-8 text-center hover:border-forest-400 dark:border-slate-600">
            <UploadCloud className="h-8 w-8 text-slate-400" />
            <span className="text-sm text-slate-500">
              {file ? file.name : 'Pnerp_CM_3.xlsx / Pnerp_CU_3.xlsx (.xlsx)'}
            </span>
            <input
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setStep('upload'); setParsed(null); }}
            />
          </label>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">
              {t('works:filters.entity')} ({t('common:optional', { defaultValue: 'opțional' })})
            </label>
            <input
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
              placeholder="ex. Cimişlia"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800"
            />
            <p className="mt-1 text-xs text-slate-400">
              {t('works:importHint', { defaultValue: 'Dacă e gol, entitatea e derivată din codul IS_UP (ex. CM).' })}
            </p>
          </div>
          <div className="flex justify-end">
            <Button onClick={onParse} disabled={!file || parseMut.isPending} loading={parseMut.isPending}>
              {t('common:actions.next', { defaultValue: 'Continuă' })}
            </Button>
          </div>
        </CardBody>
      </Card>

      {step === 'preview' && parsed && (
        <Card>
          <CardHeader className="flex flex-wrap items-center justify-between gap-2 text-sm font-semibold">
            <span>
              {parsed.sheet} · {parsed.validCount} ✓ / {parsed.invalidCount} ✗
              {parsed.entitateSilvica ? ` · ${parsed.entitateSilvica}` : ''}
            </span>
            <span className="text-xs font-normal text-slate-500">
              baza {parsed.stats.baza} · arhiv {parsed.stats.arhiv}
              {parsed.stats.totalMismatches > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-amber-600">
                  <AlertTriangle className="h-3 w-3" /> {parsed.stats.totalMismatches}
                </span>
              )}
            </span>
          </CardHeader>
          <CardBody className="overflow-auto p-0">
            <table className="w-full border-collapse text-xs">
              <thead className="bg-slate-50 text-left uppercase text-slate-500 dark:bg-slate-800">
                <tr>
                  <th className="px-2 py-2">#</th>
                  <th className="px-2 py-2">src</th>
                  <th className="px-2 py-2">{t('works:table.year')}</th>
                  <th className="px-2 py-2">{t('works:table.planFapt')}</th>
                  <th className="px-2 py-2">{t('works:table.primarie')}</th>
                  <th className="px-2 py-2">UCF+</th>
                  <th className="px-2 py-2">{t('works:table.workType')}</th>
                  <th className="px-2 py-2 text-right">{t('works:table.total')}</th>
                </tr>
              </thead>
              <tbody>
                {parsed.preview.map((r) => (
                  <tr key={`${r.source}-${r.row}`} className={r.valid ? '' : 'bg-red-50 dark:bg-red-900/10'}>
                    <td className="px-2 py-1">{r.row}</td>
                    <td className="px-2 py-1">{r.source}</td>
                    <td className="px-2 py-1">{r.year ?? '—'}</td>
                    <td className="px-2 py-1">{r.planFapt ?? '—'}</td>
                    <td className="px-2 py-1">{r.primarieComposite ?? '—'}</td>
                    <td className="px-2 py-1">{r.ucfPlus ?? '—'}</td>
                    <td className="max-w-[220px] truncate px-2 py-1" title={r.workType ?? ''}>{r.workType ?? '—'}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.totalFinal ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
          <CardBody className="flex justify-end">
            <Button onClick={onCommit} disabled={commitMut.isPending} loading={commitMut.isPending}>
              {t('import:commit.confirm', { defaultValue: 'Confirmă importul' })}
            </Button>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
